import { useMemo } from "react"

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(value || 0))
}

function formatPct(value) {
  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(Number(value || 0))
}

function buildSingleClinicReadings({
  summary,
  scoreRow,
  topFinancialAlerts,
  topExecutiveActions,
  weeklyForecast,
  saturation
}) {
  const readings = []

  const production = Number(summary?.production || 0)
  const pending = Number(summary?.pending || 0)
  const occupation = Number(summary?.avgOccupation || 0)
  const collectionRate = Number(summary?.collectionRate || 0)
  const appointments = Number(summary?.appointments || 0)
  const projectedWeek = Number(weeklyForecast?.totalProjectedRevenue || 0)

  if (scoreRow?.status === "CRITICAL") {
    readings.push("La clínica requiere intervención inmediata en cobranza, ocupación o cartera.")
  } else if (scoreRow?.status === "WARNING") {
    readings.push("La clínica está operando, pero hay señales de atención que conviene corregir hoy.")
  } else {
    readings.push("La lectura general del día es estable y sin focos rojos mayores.")
  }

  if (production > 0 && pending >= production * 0.4) {
    readings.push("La cartera pendiente ya es alta respecto a la producción del día.")
  } else if (pending > 0 && production === 0) {
    readings.push("Hay pendiente acumulado sin producción registrada en el día.")
  }

  if (collectionRate > 0 && collectionRate < 60) {
    readings.push("La eficiencia de cobro está baja; conviene reforzar seguimiento y cierre en caja.")
  } else if (collectionRate >= 85) {
    readings.push("La eficiencia de cobro va fuerte y permite cerrar mejor el día.")
  }

  if (appointments > 0 && occupation < 45) {
    readings.push("La agenda está floja; hay espacio para recall, promociones o reactivación.")
  } else if (occupation >= 90) {
    readings.push("La operación está muy cargada; cuida retrasos, cuellos de botella y tiempos muertos mal usados.")
  }

  const saturatedDoctors = (saturation || []).filter(
    (row) => row.level === "SATURATED" || row.level === "HIGH"
  ).length

  if (saturatedDoctors > 0) {
    readings.push(`Hay ${saturatedDoctors} doctor${saturatedDoctors === 1 ? "" : "es"} con presión operativa alta.`)
  }

  if (projectedWeek > 0) {
    readings.push("El pronóstico semanal te permite anticipar carga e ingresos antes de que se te descontrole la agenda.")
  }

  if ((topFinancialAlerts || []).length > 0) {
    readings.push(`Tienes ${(topFinancialAlerts || []).length} alerta${(topFinancialAlerts || []).length === 1 ? "" : "s"} financiera${(topFinancialAlerts || []).length === 1 ? "" : "s"} prioritaria${(topFinancialAlerts || []).length === 1 ? "" : "s"}.`)
  }

  if ((topExecutiveActions || []).length > 0) {
    readings.push(`Hay ${(topExecutiveActions || []).length} acción${(topExecutiveActions || []).length === 1 ? "" : "es"} recomendada${(topExecutiveActions || []).length === 1 ? "" : "s"} para atacar hoy.`)
  }

  return Array.from(new Set(readings)).slice(0, 6)
}

export default function useDashboardViewModel({
  bundle,
  clinicId,
  financialAlerts,
  executiveActions,
  executiveScore,
  saturation,
  weeklyForecast
}) {
  const summary = useMemo(() => {
    return bundle?.summary || {
      clinics: 0,
      doctors: 0,
      appointments: 0,
      production: 0,
      collected: 0,
      pending: 0,
      avgOccupation: 0,
      collectionRate: 0
    }
  }, [bundle])

  const executiveRows = useMemo(
    () => (Array.isArray(bundle?.executive) ? bundle.executive : []),
    [bundle]
  )

  const insights = bundle?.insights || null
  const highlights = bundle?.highlights || {
    topClinic: null,
    highestPendingClinic: null,
    lowestOccupationClinic: null
  }

  const isGlobal = clinicId === "ALL"

  const topFinancialAlerts = useMemo(() => {
    return (financialAlerts || [])
      .filter((alert) => isGlobal || String(alert.clinicId) === String(clinicId))
      .slice(0, 3)
  }, [financialAlerts, clinicId, isGlobal])

  const topExecutiveActions = useMemo(() => {
    return (executiveActions || [])
      .filter((action) => isGlobal || String(action.clinicId) === String(clinicId))
      .slice(0, 6)
  }, [executiveActions, clinicId, isGlobal])

  const filteredExecutiveScore = useMemo(() => {
    return (executiveScore || []).filter(
      (row) => isGlobal || String(row.clinicId) === String(clinicId)
    )
  }, [executiveScore, clinicId, isGlobal])

  const singleClinicScoreRow = !isGlobal ? filteredExecutiveScore[0] || null : null

  const saturatedDoctorsCount = useMemo(() => {
    return (saturation || []).filter(
      (row) => row.level === "SATURATED" || row.level === "HIGH"
    ).length
  }, [saturation])

  const lowLoadDoctorsCount = useMemo(() => {
    return (saturation || []).filter((row) => row.level === "LOW").length
  }, [saturation])

  const executiveTopKpis = useMemo(() => {
    return [
      {
        title: "Pendiente",
        value: `$${formatMoney(summary.pending)}`,
        helper: "Cartera actual"
      },
      {
        title: "Cobro",
        value: `${formatPct(summary.collectionRate)}%`,
        helper: "Eficiencia del día"
      },
      {
        title: "Alertas",
        value: String(topFinancialAlerts.length),
        helper: "Focos financieros"
      },
      {
        title: "Acciones",
        value: String(topExecutiveActions.length),
        helper: "Prioridades activas"
      }
    ]
  }, [summary.pending, summary.collectionRate, topFinancialAlerts.length, topExecutiveActions.length])

  const operativeTopKpis = useMemo(() => {
    return [
      {
        title: "Citas",
        value: formatMoney(summary.appointments),
        helper: "Agenda del día"
      },
      {
        title: "Ocupación",
        value: `${formatPct(summary.avgOccupation)}%`,
        helper: "Carga operativa"
      },
      {
        title: "Presión alta",
        value: String(saturatedDoctorsCount),
        helper: "Doctores exigidos"
      },
      {
        title: "Baja carga",
        value: String(lowLoadDoctorsCount),
        helper: "Espacio recuperable"
      }
    ]
  }, [
    summary.appointments,
    summary.avgOccupation,
    saturatedDoctorsCount,
    lowLoadDoctorsCount
  ])

  const singleClinicReadings = useMemo(() => {
    return buildSingleClinicReadings({
      summary,
      scoreRow: singleClinicScoreRow,
      topFinancialAlerts,
      topExecutiveActions,
      weeklyForecast,
      saturation
    })
  }, [
    summary,
    singleClinicScoreRow,
    topFinancialAlerts,
    topExecutiveActions,
    weeklyForecast,
    saturation
  ])

  return {
    summary,
    executiveRows,
    insights,
    highlights,
    topFinancialAlerts,
    topExecutiveActions,
    filteredExecutiveScore,
    singleClinicScoreRow,
    saturatedDoctorsCount,
    lowLoadDoctorsCount,
    executiveTopKpis,
    operativeTopKpis,
    singleClinicReadings
  }
}